import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Users, DollarSign, AlertTriangle, Shield, TrendingUp, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi, type DashboardStats } from '../services/api';



export function AdminDashboardIndex() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getDashboardStats();
      if (response.success && response.data) {
        setStats(response.data);
      } else {
        toast.error(response.error || 'Failed to load dashboard data');
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex-1 p-6 lg:p-8 flex items-center justify-center min-h-[500px]">
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 rounded-xl overflow-hidden border-2 border-purple-500/50 relative animate-pulse">
            <img src="/images/avatars/ravan_ultimate.png" alt="Loading" className="w-full h-full object-cover" />
          </div>
          <p className="text-sm font-semibold tracking-widest uppercase text-muted-foreground">RAVAN Loading Metrics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-medium tracking-tight text-foreground">Platform Overview</h1>
        <p className="text-sm text-muted-foreground">High-level metrics for your SaaS platform</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Total Tenants</p>
                <p className="text-3xl font-medium text-foreground">{stats?.totalTenants || 0}</p>
                <p className="text-xs font-medium text-muted-foreground mt-1">{stats?.activeTenants || 0} active workspaces</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20">
                <Users className="h-5 w-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Total Revenue</p>
                <p className="text-3xl font-medium text-emerald-400">
                  {stats ? formatCurrency(stats.totalRevenue) : '$0'}
                </p>
                <p className="text-xs font-medium text-muted-foreground mt-1">All time collected</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
                <DollarSign className="h-5 w-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">Failed Payments</p>
                <p className="text-3xl font-medium text-foreground">{stats?.failedPayments || 0}</p>
                <p className="text-xs font-medium text-muted-foreground mt-1">Requires attention</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-rose-500/10 flex items-center justify-center border border-rose-500/20">
                <AlertTriangle className="h-5 w-5 text-rose-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1">System Health</p>
                <p className="text-3xl font-medium text-foreground">{stats?.systemHealth === 'healthy' ? '98%' : '85%'}</p>
                <p className="text-xs font-medium text-muted-foreground mt-1">Operational uptime</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-indigo-500/10 flex items-center justify-center border border-indigo-500/20">
                <Shield className="h-5 w-5 text-indigo-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pt-4">
         <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Recent Activity</CardTitle>
            <CardDescription className="text-muted-foreground">Latest actions across all tenants</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <Activity className="h-8 w-8 text-slate-600 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">Activity feed coming soon</p>
              <p className="text-xs text-muted-foreground mt-1">Audit logs module is being integrated.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900 border-slate-800 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg text-foreground">Revenue Growth</CardTitle>
            <CardDescription className="text-muted-foreground">Monthly recurring revenue trends</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <TrendingUp className="h-8 w-8 text-slate-600 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">Chart data processing</p>
              <p className="text-xs text-muted-foreground mt-1">RAVAN Analyst is aggregating the data.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
